import type { FunctionComponent } from 'react';
import { useCallback, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';

import ArrowLeftIcon from '@atlaskit/icon/core/arrow-left';
import ArrowRightIcon from '@atlaskit/icon/core/arrow-right';
import type { SourceType } from '@trello/analytics-types';
import { Analytics, formatContainers } from '@trello/atlassian-analytics';
import { intl } from '@trello/i18n';
import { useBoardId, useCardId, useListId } from '@trello/id-context';
import { Button } from '@trello/nachos/button';
import { showFlag } from '@trello/nachos/experimental-flags';
import { SparkleIcon } from '@trello/nachos/icons/sparkle';

import { useCardMove } from 'app/src/components/BoardListView/useCardMove';
import { useCurrentCardAttributesFragment } from './CurrentCardFragment.generated';
import type { SuggestedList } from './useSuggestedMoves';
import { useSuggestedMoves } from './useSuggestedMoves';

import * as styles from './SuggestedMoves.module.less';

interface SuggestedMovesProps {
  onClose: () => void;
  source: SourceType;
}

export const SuggestedMoves: FunctionComponent<SuggestedMovesProps> = ({
  onClose,
  source,
}) => {
  const cardId = useCardId();
  const boardId = useBoardId();
  const listId = useListId();

  const { data: currentCardData } = useCurrentCardAttributesFragment({
    from: { id: cardId },
    optimistic: true,
  });

  const { getSuggestedMoves } = useSuggestedMoves();
  const suggestions = getSuggestedMoves();

  useEffect(() => {
    if (suggestions?.length) {
      Analytics.sendViewedComponentEvent({
        componentType: 'section',
        componentName: 'moveCardSuggestionSection',
        source,
        containers: formatContainers({ boardId, cardId, listId }),
        attributes: {
          suggestionCount: suggestions?.length,
          cardIsClosed: currentCardData?.closed,
          cardIsTemplate: currentCardData?.isTemplate,
          isModernizedMoveCardPopover: true,
        },
      });
    }
  }, [
    boardId,
    cardId,
    listId,
    suggestions?.length,
    source,
    currentCardData?.closed,
    currentCardData?.isTemplate,
  ]);

  const { moveCard } = useCardMove();

  const onSubmitMove = useCallback(
    async (targetListId: string, targetIndex: number) => {
      onClose();
      try {
        await moveCard({ cardId, listId: targetListId, index: targetIndex });

        Analytics.sendTrackEvent({
          action: 'moved',
          actionSubject: 'card',
          source,
          attributes: {
            sameBoard: true,
            suggested: true,
            cardIsClosed: currentCardData?.closed,
            cardIsTemplate: currentCardData?.isTemplate,
            isModernizedMoveCardPopover: true,
          },
          containers: formatContainers({ boardId, cardId, listId }),
        });
      } catch {
        showFlag({
          appearance: 'error',
          id: 'card-move-popover-move-submit',
          isAutoDismiss: true,
          title: intl.formatMessage({
            id: 'templates.popover_move_card.could-not-move-card',
            defaultMessage: 'Moving card failed',
            description: 'Moving card failed error flag message',
          }),
        });
      }
    },
    [
      boardId,
      cardId,
      listId,
      moveCard,
      onClose,
      source,
      currentCardData?.closed,
      currentCardData?.isTemplate,
    ],
  );

  if (!suggestions?.length) {
    return null;
  }

  return (
    <div className={styles.popoverSection}>
      <h4 className={styles.header}>
        <SparkleIcon size="small" />
        <FormattedMessage
          id="templates.popover_move_card.suggested"
          defaultMessage="Suggested"
          description="Suggested moves on move card popover"
        />
      </h4>
      <div className={styles.wrapper}>
        {suggestions.map((list: SuggestedList) => {
          return (
            <Button
              key={list.id}
              iconBefore={
                list.direction === 'left' ? (
                  <ArrowLeftIcon spacing="spacious" label="" />
                ) : (
                  <ArrowRightIcon spacing="spacious" label="" />
                )
              }
              className={styles.button}
              onClick={() => onSubmitMove(list.id, list.targetIndex)}
            >
              <div className={styles.name}>{list.name}</div>
            </Button>
          );
        })}
      </div>
    </div>
  );
};
